import { getInitData } from './telegram';
import type { Entry, Summary, Reports, UserSettings, Preset, Recurring, Budget, Debt, Goal, Currency, Kind } from './types';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Telegram-Init-Data': getInitData(),
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let msg = `${res.status} ${res.statusText}`;
    try {
      const j = JSON.parse(text);
      if (j?.error) msg = j.error;
    } catch { /* not json */ }
    throw new Error(msg);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export interface CreateEntry {
  kind: Exclude<Kind, 'exchange'>;
  currency: Currency;
  amount: number;          // positive number; sign is derived from kind
  category?: string | null;
  note?: string | null;
  occurred_at?: string;
  photo_url?: string | null;
}

export interface CreateExchange {
  from_currency: Currency;
  from_amount: number;
  to_currency: Currency;
  to_amount: number;
  rate: number;
  occurred_at?: string;
}

export interface ListParams {
  cursor?: number;
  limit?: number;
  currency?: Currency;
  kind?: Kind;
  from?: string;
  to?: string;
  q?: string;
}

export const api = {
  summary: () => request<Summary>('/api/summary'),
  reports: () => request<Reports>('/api/reports'),
  list: (p: ListParams = {}) => {
    const q = new URLSearchParams();
    if (p.cursor != null) q.set('cursor', String(p.cursor));
    if (p.limit != null)  q.set('limit',  String(p.limit));
    if (p.currency)       q.set('currency', p.currency);
    if (p.kind)           q.set('kind', p.kind);
    if (p.from)           q.set('from', p.from);
    if (p.to)             q.set('to', p.to);
    if (p.q)              q.set('q', p.q);
    const qs = q.toString();
    return request<{ entries: Entry[]; next_cursor: number | null }>(
      '/api/entries' + (qs ? `?${qs}` : ''),
    );
  },
  create: (body: CreateEntry) =>
    request<Entry>('/api/entries', { method: 'POST', body: JSON.stringify(body) }),
  exchange: (body: CreateExchange) =>
    request<{ entries: Entry[] }>('/api/exchange', { method: 'POST', body: JSON.stringify(body) }),
  setStartingBalance: (usd: number, syp: number) =>
    request<{ entries: Entry[] }>('/api/starting-balance', {
      method: 'POST',
      body: JSON.stringify({ usd, syp }),
    }),
  // Omit a currency to leave it untouched; only provided currencies are reconciled.
  reconcile: (usd?: number, syp?: number) =>
    request<{ entries: Entry[]; message?: string }>('/api/reconcile', {
      method: 'POST',
      body: JSON.stringify({
        ...(usd !== undefined ? { usd } : {}),
        ...(syp !== undefined ? { syp } : {}),
      }),
    }),
  notifyToday: () =>
    request<{ sent: true }>('/api/notify-today', { method: 'POST' }),
  sendTestReminder: () =>
    request<{ sent: true }>('/api/test-reminder', { method: 'POST' }),
  getSettings: () => request<UserSettings>('/api/settings'),
  updateSettings: (patch: Partial<Pick<UserSettings, 'reminder_enabled' | 'reminder_time_local' | 'tz_offset_minutes'>>) =>
    request<UserSettings>('/api/settings', { method: 'PUT', body: JSON.stringify(patch) }),
  remove: (id: number) =>
    request<void>(`/api/entries/${id}`, { method: 'DELETE' }),
  edit: (id: number, patch: { amount?: number; currency?: Currency; category?: string | null; note?: string | null; occurred_at?: string; photo_url?: string | null }) =>
    request<Entry>(`/api/entries/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  refundEntry: (id: number, amount: number, from?: string) =>
    request<Entry>(`/api/entries/${id}/refund`, {
      method: 'POST',
      body: JSON.stringify({ amount, from }),
    }),
  listCategories: () =>
    request<{ categories: string[]; is_default: boolean }>('/api/categories'),
  addCategory: (name: string) =>
    request<{ ok: true }>('/api/categories', { method: 'POST', body: JSON.stringify({ name }) }),
  removeCategory: (name: string) =>
    request<void>(`/api/categories/${encodeURIComponent(name)}`, { method: 'DELETE' }),
  listPresets: () =>
    request<{ presets: Preset[] }>('/api/presets'),
  addPreset: (body: { label: string; emoji?: string; currency: Currency; amount: number; kind?: 'income' | 'expense'; category?: string | null; note?: string | null }) =>
    request<Preset>('/api/presets', { method: 'POST', body: JSON.stringify(body) }),
  removePreset: (id: number) =>
    request<void>(`/api/presets/${id}`, { method: 'DELETE' }),
  usePreset: (id: number) =>
    request<Entry>(`/api/presets/${id}`, { method: 'POST' }),
  listRecurring: () =>
    request<{ recurring: Recurring[] }>('/api/recurring'),
  addRecurring: (body: { label: string; currency: Currency; amount: number; kind: 'income' | 'expense'; category?: string | null; note?: string | null; day_of_month: number; active?: boolean }) =>
    request<Recurring>('/api/recurring', { method: 'POST', body: JSON.stringify(body) }),
  updateRecurring: (id: number, patch: Partial<Pick<Recurring, 'label' | 'amount' | 'day_of_month' | 'active' | 'category' | 'note'>>) =>
    request<Recurring>(`/api/recurring/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  removeRecurring: (id: number) =>
    request<void>(`/api/recurring/${id}`, { method: 'DELETE' }),

  // Budgets
  listBudgets: () => request<{ budgets: Budget[] }>('/api/budgets'),
  putBudget: (category: string, currency: Currency, monthly_limit: number) =>
    request<Budget>('/api/budgets', { method: 'PUT', body: JSON.stringify({ category, currency, monthly_limit }) }),
  removeBudget: (category: string, currency: Currency) =>
    request<void>(`/api/budgets?category=${encodeURIComponent(category)}&currency=${currency}`, { method: 'DELETE' }),

  // Debts
  listDebts: () => request<{ debts: Debt[] }>('/api/debts'),
  addDebt: (body: { direction: 'owed_to_me' | 'i_owe'; counterparty: string; currency: Currency; amount: number; note?: string | null }) =>
    request<Debt>('/api/debts', { method: 'POST', body: JSON.stringify(body) }),
  updateDebt: (id: number, patch: { status?: 'open' | 'settled'; counterparty?: string; amount?: number; note?: string | null }) =>
    request<Debt>(`/api/debts/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  removeDebt: (id: number) =>
    request<void>(`/api/debts/${id}`, { method: 'DELETE' }),

  // Goals
  listGoals: () => request<{ goals: Goal[] }>('/api/goals'),
  addGoal: (body: { name: string; currency: Currency; target_amount: number }) =>
    request<Goal>('/api/goals', { method: 'POST', body: JSON.stringify(body) }),
  contributeGoal: (id: number, amount: number) =>
    request<Goal>(`/api/goals/${id}`, { method: 'PUT', body: JSON.stringify({ contribute: amount }) }),
  updateGoal: (id: number, patch: { name?: string; target_amount?: number; saved_amount?: number }) =>
    request<Goal>(`/api/goals/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  removeGoal: (id: number) =>
    request<void>(`/api/goals/${id}`, { method: 'DELETE' }),

  // Export all entries as a CSV — delivered as a document via the bot to the user's chat.
  exportCsv: () => request<{ sent: true; count: number }>('/api/export', { method: 'POST' }),

  // Receipt photo upload — returns the public URL.
  uploadPhoto: async (file: File): Promise<string> => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch('/api/upload', {
      method: 'POST',
      headers: { 'X-Telegram-Init-Data': getInitData() },
      body: form,
    });
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      let msg = `${res.status}`;
      try { const j = JSON.parse(t); if (j?.error) msg = j.error; } catch { /* */ }
      throw new Error(msg);
    }
    const j = await res.json() as { url: string };
    return j.url;
  },
};
